/**
 * Converts a PascalCase string to snake_case.
 *
 * Handles consecutive uppercase letters correctly by inserting underscores
 * between word boundaries. For example, "AboutATopic" becomes "about_a_topic".
 *
 * @param str - The PascalCase string to convert
 * @returns The snake_case version of the input string
 * @example
 * pascalToSnakeCase('MyClass') // 'my_class'
 * pascalToSnakeCase('AboutATopic') // 'about_a_topic'
 * pascalToSnakeCase('XMLHttpRequest') // 'xml_http_request'
 */
export function pascalToSnakeCase(str) {
    // Step 1: Insert underscore between lowercase/digit and uppercase letters
    // This handles cases like "MyClass" -> "My_Class"
    const withLowercaseBoundaries = str.replace(/([a-z\d])([A-Z])/g, '$1_$2');
    // Step 2: Insert underscore between multiple consecutive uppercase letters and an uppercase+lowercase sequence
    // This handles cases like "XMLHttpRequest" -> "XML_HttpRequest" and "AboutATopic" -> "About_ATopic"
    const withUppercaseBoundaries = withLowercaseBoundaries.replace(/([A-Z]+)([A-Z][a-z])/g, '$1_$2');
    // Step 3: Convert the entire string to lowercase
    return withUppercaseBoundaries.toLowerCase();
}
/**
 * Converts a camelCase string to snake_case.
 *
 * Inserts an underscore before each uppercase letter and converts it to lowercase.
 *
 * @param str - The camelCase string to convert
 * @returns The snake_case version of the input string
 * @example
 * camelToSnakeCase('myClass') // 'my_class'
 * camelToSnakeCase('userProfile') // 'user_profile'
 */
export function camelToSnakeCase(str) {
    // Replace each uppercase letter with an underscore followed by the lowercase version
    return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}
/**
 * Converts a PascalCase string to camelCase.
 *
 * Simply lowercases the first character of the string.
 *
 * @param str - The PascalCase string to convert
 * @returns The camelCase version of the input string
 * @example
 * pascalToCamelCase('MyClass') // 'myClass'
 * pascalToCamelCase('UserProfile') // 'userProfile'
 */
export function pascalToCamelCase(str) {
    // Lowercase the first character and keep the rest unchanged
    return str.charAt(0).toLowerCase() + str.slice(1);
}
/**
 * Converts a camelCase string to PascalCase.
 *
 * Simply uppercases the first character of the string.
 *
 * @param str - The camelCase string to convert
 * @returns The PascalCase version of the input string
 * @example
 * camelToPascalCase('myClass') // 'MyClass'
 * camelToPascalCase('userProfile') // 'UserProfile'
 */
export function camelToPascalCase(str) {
    // Uppercase the first character and keep the rest unchanged
    return str.charAt(0).toUpperCase() + str.slice(1);
}
/**
 * Converts a snake_case string to PascalCase.
 *
 * Splits the string by underscores, capitalizes each word, and joins them together.
 *
 * @param str - The snake_case string to convert
 * @returns The PascalCase version of the input string
 * @example
 * snakeToPascalCase('my_class') // 'MyClass'
 * snakeToPascalCase('user_profile') // 'UserProfile'
 */
export function snakeToPascalCase(str) {
    // Step 1: Split the string by underscores into individual words
    const words = str.split('_');
    // Step 2: Capitalize the first letter of each word
    const capitalizedWords = words.map((word) => camelToPascalCase(word));
    // Step 3: Join all words together without separators
    return capitalizedWords.join('');
}
/**
 * Converts a snake_case string to camelCase.
 *
 * Splits the string by underscores, keeps the first word lowercase, and capitalizes
 * subsequent words, then joins them together.
 *
 * @param str - The snake_case string to convert
 * @returns The camelCase version of the input string
 * @example
 * snakeToCamelCase('my_class') // 'myClass'
 * snakeToCamelCase('user_profile') // 'userProfile'
 */
export function snakeToCamelCase(str) {
    // Step 1: Split the string by underscores into individual words
    const words = str.split('_');
    // Step 2: Keep the first word lowercase, capitalize subsequent words
    const camelWords = words.map((word, index) => {
        if (index === 0) {
            return word; // First word stays lowercase
        }
        return camelToPascalCase(word); // Subsequent words are capitalized
    });
    // Step 3: Join all words together without separators
    return camelWords.join('');
}
/**
 * Converts a snake_case string to a human-readable label (Title Case with spaces).
 *
 * Splits the string by underscores, capitalizes each word, and joins them with spaces.
 *
 * @param str - The snake_case string to convert
 * @returns A Title Case label with spaces
 * @example
 * snakeCaseToLabel('my_class') // 'My Class'
 * snakeCaseToLabel('user_profile') // 'User Profile'
 */
export function snakeCaseToLabel(str) {
    // Step 1: Split the string by underscores into individual words
    const words = str.split('_');
    // Step 2: Capitalize the first letter of each word
    const capitalizedWords = words.map((word) => word.charAt(0).toUpperCase() + word.slice(1));
    // Step 3: Join words with spaces
    return capitalizedWords.join(' ');
}
/**
 * Converts a camelCase string to a human-readable label (Title Case with spaces).
 *
 * First converts to snake_case, then to a label format.
 *
 * @param str - The camelCase string to convert
 * @returns A Title Case label with spaces
 * @example
 * camelCaseToLabel('myClass') // 'My Class'
 * camelCaseToLabel('userProfile') // 'User Profile'
 */
export function camelCaseToLabel(str) {
    // Convert camelCase -> snake_case -> label
    return snakeCaseToLabel(camelToSnakeCase(str));
}
/**
 * Converts a PascalCase string to a human-readable label (Title Case with spaces).
 *
 * First converts to snake_case, then to a label format.
 *
 * @param str - The PascalCase string to convert
 * @returns A Title Case label with spaces
 * @example
 * pascalCaseToLabel('MyClass') // 'My Class'
 * pascalCaseToLabel('UserProfile') // 'User Profile'
 */
export function pascalCaseToLabel(str) {
    // Convert PascalCase -> snake_case -> label
    return snakeCaseToLabel(pascalToSnakeCase(str));
}
/**
 * Converts a PascalCase string to kebab-case.
 *
 * First converts to snake_case, then replaces underscores with hyphens.
 *
 * @param str - The PascalCase string to convert
 * @returns The kebab-case version of the input string
 * @example
 * pascalCaseToKebab('MyClass') // 'my-class'
 * pascalCaseToKebab('UserProfile') // 'user-profile'
 */
export function pascalCaseToKebab(str) {
    // Step 1: Convert PascalCase to snake_case
    const snakeCase = pascalToSnakeCase(str);
    // Step 2: Replace underscores with hyphens
    return snakeCase.replace(/_/g, '-');
}
/**
 * Converts a camelCase string to kebab-case.
 *
 * First converts to snake_case, then replaces underscores with hyphens.
 *
 * @param str - The camelCase string to convert
 * @returns The kebab-case version of the input string
 * @example
 * camelCaseToKebab('myClass') // 'my-class'
 * camelCaseToKebab('userProfile') // 'user-profile'
 */
export function camelCaseToKebab(str) {
    // Step 1: Convert camelCase to snake_case
    const snakeCase = camelToSnakeCase(str);
    // Step 2: Replace underscores with hyphens
    return snakeCase.replace(/_/g, '-');
}
/**
 * Converts a snake_case string to kebab-case.
 *
 * Simply replaces underscores with hyphens.
 *
 * @param str - The snake_case string to convert
 * @returns The kebab-case version of the input string
 * @example
 * snakeCaseToKebab('my_class') // 'my-class'
 * snakeCaseToKebab('user_profile') // 'user-profile'
 */
export function snakeCaseToKebab(str) {
    // Replace all underscores with hyphens
    return str.replace(/_/g, '-');
}
/**
 * Converts a kebab-case string to snake_case.
 *
 * Simply replaces hyphens with underscores.
 *
 * @param str - The kebab-case string to convert
 * @returns The snake_case version of the input string
 * @example
 * kebabToSnakeCase('my-class') // 'my_class'
 * kebabToSnakeCase('user-profile') // 'user_profile'
 */
export function kebabToSnakeCase(str) {
    // Replace all hyphens with underscores
    return str.replace(/-/g, '_');
}
/**
 * Converts a kebab-case string to a human-readable label (Title Case with spaces).
 *
 * First converts to snake_case, then to a label format.
 *
 * @param str - The kebab-case string to convert
 * @returns A Title Case label with spaces
 * @example
 * kebabCaseToLabel('my-class') // 'My Class'
 * kebabCaseToLabel('user-profile') // 'User Profile'
 */
export function kebabCaseToLabel(str) {
    // Convert kebab-case -> snake_case -> label
    return snakeCaseToLabel(kebabToSnakeCase(str));
}
/**
 * Converts a kebab-case string to camelCase.
 *
 * First converts to snake_case, then to camelCase.
 *
 * @param str - The kebab-case string to convert
 * @returns The camelCase version of the input string
 * @example
 * kebabToCamelCase('my-class') // 'myClass'
 * kebabToCamelCase('user-profile') // 'userProfile'
 */
export function kebabToCamelCase(str) {
    // Convert kebab-case -> snake_case -> camelCase
    return snakeToCamelCase(kebabToSnakeCase(str));
}
/**
 * Converts a kebab-case string to PascalCase.
 *
 * First converts to snake_case, then to PascalCase.
 *
 * @param str - The kebab-case string to convert
 * @returns The PascalCase version of the input string
 * @example
 * kebabToPascalCase('my-class') // 'MyClass'
 * kebabToPascalCase('user-profile') // 'UserProfile'
 */
export function kebabToPascalCase(str) {
    // Convert kebab-case -> snake_case -> PascalCase
    return snakeToPascalCase(kebabToSnakeCase(str));
}
/**
 * Validates if a string is in valid PascalCase format.
 *
 * PascalCase must start with an uppercase letter or digit, followed by
 * alphanumeric characters. No underscores, hyphens, or spaces allowed.
 *
 * @param str - The string to validate
 * @returns True if the string is valid PascalCase, false otherwise
 * @example
 * isValidPascalCase('MyClass') // true
 * isValidPascalCase('myClass') // false (starts with lowercase)
 * isValidPascalCase('My_Class') // false (contains underscore)
 */
export function isValidPascalCase(str) {
    // Regex: starts with uppercase letter or digit, followed by alphanumeric characters
    const regex = /^[A-Z0-9][a-zA-Z0-9]*$/;
    return regex.test(str);
}
/**
 * Validates if a string is in valid camelCase format.
 *
 * camelCase must start with a lowercase letter or digit, followed by
 * alphanumeric characters. No underscores, hyphens, or spaces allowed.
 *
 * @param str - The string to validate
 * @returns True if the string is valid camelCase, false otherwise
 * @example
 * isValidCamelCase('myClass') // true
 * isValidCamelCase('MyClass') // false (starts with uppercase)
 * isValidCamelCase('my_class') // false (contains underscore)
 */
export function isValidCamelCase(str) {
    // Regex: starts with lowercase letter or digit, followed by alphanumeric characters
    const regex = /^[a-z0-9][a-zA-Z0-9]*$/;
    return regex.test(str);
}
/**
 * Validates if a string is in valid snake_case format.
 *
 * snake_case must consist of lowercase letters and digits, separated by
 * single underscores. Must start and end with alphanumeric characters.
 *
 * @param str - The string to validate
 * @returns True if the string is valid snake_case, false otherwise
 * @example
 * isValidSnakeCase('my_class') // true
 * isValidSnakeCase('my_class_name') // true
 * isValidSnakeCase('MyClass') // false (contains uppercase)
 * isValidSnakeCase('my__class') // false (consecutive underscores)
 * isValidSnakeCase('_my_class') // false (starts with underscore)
 */
export function isValidSnakeCase(str) {
    // Regex: one or more lowercase/digits, optionally followed by underscore and more lowercase/digits
    const regex = /^[a-z0-9]+(_[a-z0-9]+)*$/;
    return regex.test(str);
}
/**
 * Indents each line of a multiline string by a specified number of tabs.
 *
 * Splits the string by newlines, adds the specified number of tab characters
 * to the beginning of each line, and joins them back together.
 *
 * @param inputString - The multiline string to indent
 * @param numberOfTabs - The number of tab characters to add to each line
 * @returns The indented multiline string
 * @example
 * indentMultilineString('line1\nline2', 1) // '\tline1\n\tline2'
 * indentMultilineString('line1\nline2', 2) // '\t\tline1\n\t\tline2'
 */
export function indentMultilineString(inputString, numberOfTabs) {
    // Early return for empty strings
    if (inputString === '') {
        return '';
    }
    // Step 1: Create a string with the specified number of tab characters
    const tabString = '\t'.repeat(numberOfTabs);
    // Step 2: Split the input string into individual lines
    const lines = inputString.split('\n');
    // Step 3: Prepend tabs to each line
    const indentedLines = lines.map((line) => tabString + line);
    // Step 4: Join the indented lines back together with newlines
    return indentedLines.join('\n');
}
/**
 * Converts a string that is entirely uppercase to lowercase.
 *
 * If the input string is all uppercase (e.g., "API", "XML"), it converts it to
 * lowercase. Otherwise, returns the string unchanged.
 *
 * @param inputString - The string to potentially convert
 * @returns The lowercase version if all uppercase, otherwise the original string
 * @example
 * allCapsToLowercase('API') // 'api'
 * allCapsToLowercase('XML') // 'xml'
 * allCapsToLowercase('MyClass') // 'MyClass' (unchanged)
 */
export function allCapsToLowercase(inputString) {
    // If the string is entirely uppercase, convert to lowercase
    if (inputString.toUpperCase() === inputString) {
        return inputString.toLowerCase();
    }
    return inputString;
}
/**
 * Converts a string in any case format to snake_case.
 *
 * Handles various input formats including PascalCase, camelCase, kebab-case,
 * ALL_CAPS, and mixed formats. Normalizes the input and converts it to snake_case.
 *
 * @param input - The string in any case format to convert
 * @returns The snake_case version of the input string
 * @example
 * anyToSnakeCase('MyClass') // 'my_class'
 * anyToSnakeCase('myClass') // 'my_class'
 * anyToSnakeCase('my-class') // 'my_class'
 * anyToSnakeCase('API') // 'api'
 * anyToSnakeCase('AboutATopic') // 'about_a_topic'
 */
export function anyToSnakeCase(input) {
    // Step 1: Convert all-caps strings (like "API", "XML") to lowercase
    const normalized = allCapsToLowercase(input);
    // Step 2: Replace hyphens and existing underscores with spaces for normalization
    const withSpaces = normalized.replace(/[-_]/g, ' ');
    // Step 3: Insert space before each uppercase letter (except at the start)
    // This converts "MyClass" -> "My Class" and "AboutATopic" -> "About A Topic"
    // Handle consecutive uppercase letters by matching them as a group first
    const withWordBoundaries = withSpaces
        .replace(/([a-z\d])([A-Z])/g, '$1 $2') // Insert space between lowercase/digit and uppercase
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2'); // Insert space between consecutive uppercase and uppercase+lowercase
    // Step 4: Convert to lowercase
    const lowercased = withWordBoundaries.toLowerCase();
    // Step 5: Replace all spaces with underscores
    const withUnderscores = lowercased.replace(/ /g, '_');
    // Step 6: Replace consecutive underscores with single underscore
    const singleUnderscores = withUnderscores.replace(/__+/g, '_');
    // Step 7: Remove leading/trailing underscores
    return singleUnderscores.replace(/^_+|_+$/g, '');
}
/**
 * Converts a string in any case format to camelCase.
 *
 * First converts to snake_case, then to camelCase. Handles various input formats
 * including PascalCase, kebab-case, ALL_CAPS, and mixed formats.
 *
 * @param input - The string in any case format to convert
 * @returns The camelCase version of the input string
 * @example
 * anyToCamelCase('MyClass') // 'myClass'
 * anyToCamelCase('my-class') // 'myClass'
 * anyToCamelCase('MY_CLASS') // 'myClass'
 * anyToCamelCase('AboutATopic') // 'aboutATopic'
 */
export function anyToCamelCase(input) {
    // Step 1: Convert to snake_case first
    const snakeCase = anyToSnakeCase(input);
    // Step 2: Convert snake_case to camelCase
    return snakeToCamelCase(snakeCase);
}
/**
 * Converts a string in any case format to PascalCase.
 *
 * First converts to snake_case, then to PascalCase. Handles various input formats
 * including camelCase, kebab-case, ALL_CAPS, and mixed formats.
 *
 * @param input - The string in any case format to convert
 * @returns The PascalCase version of the input string
 * @example
 * anyToPascalCase('myClass') // 'MyClass'
 * anyToPascalCase('my-class') // 'MyClass'
 * anyToPascalCase('MY_CLASS') // 'MyClass'
 * anyToPascalCase('aboutATopic') // 'AboutATopic'
 */
export function anyToPascalCase(input) {
    // Step 1: Convert to snake_case first
    const snakeCase = anyToSnakeCase(input);
    // Step 2: Convert snake_case to PascalCase
    return snakeToPascalCase(snakeCase);
}
/**
 * Converts a string in any case format to kebab-case.
 *
 * First converts to snake_case, then to kebab-case. Handles various input formats
 * including PascalCase, camelCase, ALL_CAPS, and mixed formats.
 *
 * @param input - The string in any case format to convert
 * @returns The kebab-case version of the input string
 * @example
 * anyToKebabCase('MyClass') // 'my-class'
 * anyToKebabCase('myClass') // 'my-class'
 * anyToKebabCase('MY_CLASS') // 'my-class'
 * anyToKebabCase('AboutATopic') // 'about-a-topic'
 */
export function anyToKebabCase(input) {
    // Step 1: Convert to snake_case first
    const snakeCase = anyToSnakeCase(input);
    // Step 2: Convert snake_case to kebab-case
    return snakeCaseToKebab(snakeCase);
}
/**
 * Converts a string in any case format to a human-readable label (Title Case with spaces).
 *
 * First converts to snake_case, then to a label format. Handles various input formats
 * including PascalCase, camelCase, kebab-case, ALL_CAPS, and mixed formats.
 *
 * @param input - The string in any case format to convert
 * @returns A Title Case label with spaces
 * @example
 * anyToLabelCase('MyClass') // 'My Class'
 * anyToLabelCase('myClass') // 'My Class'
 * anyToLabelCase('my-class') // 'My Class'
 * anyToLabelCase('MY_CLASS') // 'My Class'
 * anyToLabelCase('AboutATopic') // 'About A Topic'
 */
export function anyToLabelCase(input) {
    // Step 1: Convert to snake_case first
    const snakeCase = anyToSnakeCase(input);
    // Step 2: Convert snake_case to label format
    return snakeCaseToLabel(snakeCase);
}
